import { useState } from "react";
import {
  Building2,
  MapPin,
  ShieldCheck,
  Plus,
  Search,
  ExternalLink,
  FileText,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useRealEstateContext } from "../providers/RealEstateContext";
import { useNavigate } from "react-router-dom";

import { ActionPopupModal } from "../misc/ActionPopupModal";

export const ProjectList = () => {
  const navigate = useNavigate();
  const { projects, properties, addProject, propertyMedia } =
    useRealEstateContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [popupInfo, setPopupInfo] = useState<{
    title: string;
    desc: string;
  } | null>(null);
  const [newProject, setNewProject] = useState({
    name: "",
    location_address: "",
    city: "Hyderabad",
    google_maps_url: "",
    rera_number: "",
  });

  const filteredProjects = projects.filter(
    (p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.location_address.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProject.name.trim()) return;
    setCreating(true);
    try {
      const created = await addProject({
        name: newProject.name.trim(),
        location_address: newProject.location_address.trim(),
        city: newProject.city.trim() || "Hyderabad",
        google_maps_url: newProject.google_maps_url.trim() || undefined,
        rera_number: newProject.rera_number.trim() || undefined,
      });
      setPopupInfo({
        title: created ? "Project Created" : "Could not create project",
        desc: created
          ? `"${created.name}" is now in the project master.`
          : "Create failed — check your connection and try again.",
      });
      if (created) {
        setNewProject({
          name: "",
          location_address: "",
          city: "Hyderabad",
          google_maps_url: "",
          rera_number: "",
        });
        setIsAddModalOpen(false);
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      {/* Header Bar */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Building2 className="w-7 h-7 text-primary" />
            Project Management & Builder Master
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage real estate developer projects, RERA credentials, site
            locations, and master inventory allocations.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/properties")}
            className="border-primary text-primary hover:bg-primary/10 font-semibold"
          >
            <Building2 className="w-4 h-4 mr-2" /> Manage Master Inventory
          </Button>
          <Button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" /> Create New Project
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-card p-4 rounded-xl border shadow-sm flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input
            placeholder="Search Project Name, City, Location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-muted/20 px-6 py-16 text-center space-y-4">
          <Building2 className="w-10 h-10 mx-auto text-muted-foreground" />
          <div className="space-y-1">
            <p className="text-lg font-semibold text-foreground">
              No projects yet
            </p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Your inventory is empty. Create a project first, then add units
              under Properties.
            </p>
          </div>
          <Button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" /> Add project
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => {
            const projectProperties = properties.filter(
              (p) => p.project_id === project.id,
            );
            const availableCount = projectProperties.filter(
              (p) => p.status === "Available",
            ).length;
            const bookedCount = projectProperties.filter(
              (p) => p.status === "Booked" || p.status === "Sold",
            ).length;

            return (
              <Card
                key={project.id}
                className="hover:shadow-md transition-shadow border-muted flex flex-col justify-between overflow-hidden"
              >
                <div>
                  <CardHeader className="pb-3 border-b bg-muted/20">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl font-bold text-foreground">
                          {project.name}
                        </CardTitle>
                        <CardDescription className="flex items-center text-xs mt-1 text-muted-foreground">
                          <MapPin className="w-3.5 h-3.5 mr-1 text-primary" />
                          {project.location_address}, {project.city}
                        </CardDescription>
                      </div>
                      <Badge
                        variant="outline"
                        className="text-xs bg-background"
                      >
                        {project.city}
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-4 text-sm">
                    {/* RERA Number */}
                    {project.rera_number && (
                      <div className="bg-emerald-50 dark:bg-emerald-950/30 p-2.5 rounded-lg border border-emerald-200 dark:border-emerald-800 flex items-center justify-between text-xs text-emerald-800 dark:text-emerald-300 font-medium">
                        <span className="flex items-center gap-1">
                          <ShieldCheck className="w-4 h-4 text-emerald-600" />{" "}
                          RERA Registered
                        </span>
                        <span className="font-mono">{project.rera_number}</span>
                      </div>
                    )}

                    {/* Inventory Metrics */}
                    <div className="grid grid-cols-3 gap-2 text-center bg-muted/40 p-3 rounded-xl border">
                      <div>
                        <span className="text-[10px] text-muted-foreground uppercase block font-semibold">
                          Total Units
                        </span>
                        <span className="text-lg font-bold text-foreground">
                          {projectProperties.length}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] text-muted-foreground uppercase block font-semibold">
                          Available
                        </span>
                        <span className="text-lg font-bold text-emerald-600">
                          {availableCount}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] text-muted-foreground uppercase block font-semibold">
                          Booked / Sold
                        </span>
                        <span className="text-lg font-bold text-primary">
                          {bookedCount}
                        </span>
                      </div>
                    </div>

                    {/* Maps Link */}
                    {project.google_maps_url && (
                      <a
                        href={project.google_maps_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline flex items-center gap-1 font-medium"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> View Google
                        Maps Location Pin
                      </a>
                    )}
                  </CardContent>
                </div>

                <CardFooter className="pt-3 border-t bg-muted/10 flex items-center justify-between gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs font-semibold"
                    onClick={() => {
                      const unitIds = new Set(
                        properties
                          .filter(
                            (p) => String(p.project_id) === String(project.id),
                          )
                          .map((p) => String(p.id)),
                      );
                      const brochure = propertyMedia.find(
                        (m) =>
                          m.media_type === "Brochure_PDF" &&
                          unitIds.has(String(m.property_id)),
                      );
                      if (brochure?.media_url) {
                        window.open(brochure.media_url, "_blank");
                        return;
                      }
                      if (project.google_maps_url) {
                        window.open(project.google_maps_url, "_blank");
                        return;
                      }
                      setPopupInfo({
                        title: "No brochure yet",
                        desc: `Add a Brochure_PDF on a unit under "${project.name}" (Edit Unit → Brochure URL), or set a Google Maps link.`,
                      });
                    }}
                  >
                    <FileText className="w-3.5 h-3.5 mr-1" /> Brochure
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-xs shadow-sm"
                    onClick={() => navigate("/properties")}
                  >
                    View Inventory <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}

      {/* CREATE PROJECT DIALOG */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Create New Developer Project
            </DialogTitle>
            <DialogDescription>
              Register a real estate project master to group unit inventory and
              RERA credentials.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={handleCreateProject}
            className="space-y-4 py-2 text-sm"
          >
            <div className="space-y-1">
              <label className="text-xs font-semibold">Project Name</label>
              <Input
                placeholder="e.g. Emerald Heights"
                value={newProject.name}
                onChange={(e) =>
                  setNewProject({ ...newProject, name: e.target.value })
                }
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold">City</label>
                <Input
                  placeholder="e.g. Hyderabad"
                  value={newProject.city}
                  onChange={(e) =>
                    setNewProject({ ...newProject, city: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold">RERA Number</label>
                <Input
                  placeholder="e.g. P02400001234"
                  value={newProject.rera_number}
                  onChange={(e) =>
                    setNewProject({
                      ...newProject,
                      rera_number: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold">
                Site Address / Location
              </label>
              <Input
                placeholder="e.g. Banjara Hills, Road No. 12"
                value={newProject.location_address}
                onChange={(e) =>
                  setNewProject({
                    ...newProject,
                    location_address: e.target.value,
                  })
                }
                required
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold">Google Maps URL</label>
              <Input
                placeholder="https://maps.google.com/..."
                value={newProject.google_maps_url}
                onChange={(e) =>
                  setNewProject({
                    ...newProject,
                    google_maps_url: e.target.value,
                  })
                }
              />
            </div>

            <DialogFooter className="pt-3 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creating || !newProject.name.trim()}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {creating ? "Saving…" : "Save Project Master"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ACTION POPUP MODAL */}
      <ActionPopupModal
        isOpen={!!popupInfo}
        onClose={() => setPopupInfo(null)}
        title={popupInfo?.title || ""}
        description={popupInfo?.desc || ""}
      />
    </div>
  );
};

export default ProjectList;
